import { useEffect, useState } from 'react';

import { Box } from '@ecoinvent/ui';

interface TourOverlayProps {
  isActive: boolean;
  targetId?: string;
  padding?: number;
  onClickOutside?: () => void;
  ariaLabel?: string;
}

interface TargetPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

const TourOverlay = ({
  isActive,
  targetId,
  padding = 8,
  onClickOutside,
  ariaLabel = 'Click to skip tour',
}: TourOverlayProps) => {
  const [targetPosition, setTargetPosition] = useState<TargetPosition | null>(null);

  useEffect(() => {
    if (!isActive || !targetId) {
      setTargetPosition(null);
      return;
    }

    let retryCount = 0;
    const maxRetries = 20;
    let retryTimeoutId: NodeJS.Timeout;

    const updatePosition = () => {
      const element = document.querySelector(`[data-tour-target="${targetId}"]`);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetPosition({
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        });
      } else if (retryCount < maxRetries) {
        retryCount++;
        retryTimeoutId = setTimeout(updatePosition, 300);
      }
    };

    const timeoutId = setTimeout(updatePosition, 100);

    const recalcIntervalId = setInterval(updatePosition, 200);
    const stopRecalcTimeoutId = setTimeout(() => {
      clearInterval(recalcIntervalId);
    }, 1500);

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(retryTimeoutId);
      clearInterval(recalcIntervalId);
      clearTimeout(stopRecalcTimeoutId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isActive, targetId, padding]);

  if (!isActive) {
    return null;
  }

  return (
    <>
      {/* Clickable overlay background */}
      <Box
        position="fixed"
        inset={0}
        zIndex="overlay"
        cursor="pointer"
        onClick={onClickOutside}
        aria-label={ariaLabel}
        bg={!targetId ? 'rgba(0, 0, 0, 0.6)' : undefined}
      />
      {/* Spotlight with rounded corners using box-shadow */}
      {targetPosition && (
        <Box
          position="fixed"
          top={`${targetPosition.top}px`}
          left={`${targetPosition.left}px`}
          width={`${targetPosition.width}px`}
          height={`${targetPosition.height}px`}
          borderRadius="lg"
          zIndex="overlay"
          pointerEvents="none"
          transition="all 0.3s ease-in-out"
          boxShadow="0 0 0 9999px rgba(0, 0, 0, 0.6)"
        />
      )}
    </>
  );
};

export default TourOverlay;
